import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { lastValueFrom, Observable } from 'rxjs';
import { ModalNewFolderComponent } from '../components/sidebar/psa/psa-filesystem/components/modal-new-folder/modal-new-folder.component';
import { TreeNode } from '../components/sidebar/psa/psa-filesystem/models/tree-models';
import { PsaFileSystemComponent } from '../components/sidebar/psa/psa-filesystem/psa-filesystem.component';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';

/**
 * Servizio per gestire tutte le fnzionalità inerenti al filesystem implementato per il PSA
 *
 * @author Alberto Bartolini
 */
@Injectable({
  providedIn: 'root'
})
export class FilesystemService {
  constructor(
    private _dialog: MatDialog,
    private _http: HttpClient,
    private _loginService: LoginService,
    private _modalService: EbwModalService,
    private _apiUrlService: ApiUrlService
  ) {}

  get _headers(): HttpHeaders {
    // Get the Authorization header with the JWT token retrieved from the login service
    return new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken()
    });
  }

  // ------------------------------------------------------------------------------------------------

  public getFilesystem(): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_GET)}`;
    const headers = { headers: this._headers };
    return this._http.get(url, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public checkElementFolder(element: any): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_CHECK_ELEMENT)}`;
    const headers = { headers: this._headers };
    return this._http.post(url, { element }, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public uploadFile(file: File, folderId: string): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FILE_CREATE)}`;
    const body = new FormData();
    body.append('file', file);
    body.append('folderId', folderId);
    body.append('isDeletable', 'true');
    body.append('isEditable', 'true');
    const headers = { headers: this._headers };
    return this._http.post(url, body, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public deleteFile(fileId: string): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FILE_DELETE)}/${fileId}`;
    const headers = { headers: this._headers };
    return this._http.delete(url, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public deleteFolder(folderId: string): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FOLDER_DELETE)}/${folderId}`;
    const headers = { headers: this._headers };
    return this._http.delete(url, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public createFolder(parentFolderId: string, name: string): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FOLDER_CREATE)}`;
    const body = { name, parentId: parentFolderId, isDeletable: true, isEditable: true };
    const headers = { headers: this._headers };
    return this._http.post(url, body, headers);
  }

  // ------------------------------------------------------------------------------------------------

  public updateFolderName(folderId: string, name: string): Observable<any> {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FOLDER_UPDATE)}/${folderId}`;
    const body = { name };
    const headers = { headers: this._headers };
    return this._http.patch(url, body, headers);
  }

  // ------------------------------------------------------------------------------------------------

  moveFile(fileId: string, folderId: string) {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_FILE_MOVE)}/${fileId}/move`;
    const body = { folderId };
    const headers = { headers: this._headers };
    return this._http.patch(url, body, headers);
  }

  // ------------------------------------------------------------------------------------------------

  async openFilesystemModal(folder?: TreeNode) {
    const ref = this._dialog.open(PsaFileSystemComponent, {
      maxWidth: '1500px',
      width: '70%',
      maxHeight: '70%',
      panelClass: ['filesystem-container'],
      hasBackdrop: true,
      data: { folder }
    });

    await lastValueFrom(ref.afterClosed());
  }

  // ------------------------------------------------------------------------------------------------

  async openModalNewFolder(data: any, parentId: string) {
    const ref = this._dialog.open(ModalNewFolderComponent, {
      width: '500px',
      panelClass: ['filesystem-container'],
      hasBackdrop: true,
      data
    });

    const res = await lastValueFrom(ref.afterClosed());

    if (!res) {
      return false;
    }

    try {
      if (res?.new) {
        await lastValueFrom(this.createFolder(parentId, res.folder.name));
      } else {
        await lastValueFrom(this.updateFolderName(res.folder.id, data.folder.name));
      }
      return true;
    } catch (error) {
      console.error(error);
      this._modalService.openErrorModal({
        title: 'Errore',
        content: error?.error?.message ?? 'Errore creazione cartella'
      });
      return false;
    }
  }

  // ------------------------------------------------------------------------------------------------

  findNode(node: TreeNode, newFolder: string): TreeNode | null {
    if (node.name === newFolder) {
      return node;
    }

    if (node.children) {
      for (const children of node.children) {
        const nodoTrovato = this.findNode(children, newFolder);
        if (nodoTrovato) {
          return nodoTrovato;
        }
      }
    }

    return null;
  }

  // ------------------------------------------------------------------------------------------------

  // Ricerca di un nodo genitore sull'albero binario
  findParent(nodo: TreeNode, nodoCliccato: TreeNode): TreeNode | null {
    // Se ci sono dei figli scendo in profondita' nell'albero
    // finche' non trovo il padre corretto
    if (nodo && nodo.children)
      for (const figlio of nodo.children) {
        if (figlio === nodoCliccato) return nodo;
        else {
          // Viene effettuata la ricorsione per scorrere l'albero
          const padrePotenziale = this.findParent(figlio, nodoCliccato);
          if (padrePotenziale) return padrePotenziale;
        }
      }
    return null;
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per la creazione della chiamata Http necessaria per aprire un file
  openFile(node: TreeNode) {
    // Qui creo gli headers necessari per comunicare con Apache
    const headers: HttpHeaders = new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken(),
      timeout: '120000',
      Accept: 'application/octet-stream'
    });

    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.FILESYSTEM_GET_FILE)}/${node.id}`;
    // Qui viene effettuata la chiamata http per l'apertura del file
    this._http.get(url, { headers, responseType: 'blob' }).subscribe({
      next: (response) => {
        const file = this.createBlob(response, node.ext);
        this.openBlob(file, node.ext, node.name);
      },
      error: (error) => {
        let message = '';
        if (error.status === 404) {
          message = 'File non trovato';
        } else {
          message = 'Si è verificato un errore durante il download del file';
        }
        this._modalService.openInfoModal({ title: 'Download file', content: message });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per creare un File-Blob in base all'estensione del File passata in input
  createBlob(response: any, ext: string): Blob {
    let type;

    switch (ext) {
      case '.pdf':
        type = 'application/pdf';
        break;
      case '.txt':
      case '.csv':
        type = 'text/plain';
        break;
      case '.doc':
      case '.docs':
        type = 'application/msword';
        break;
      case '.xls':
      case '.xlsx':
        type = 'application/vnd.ms-excel';
        break;
      case '.json':
      case '.geojson':
        type = 'application/json';
        break;
      default:
        type = 'application/octet-stream';
    }

    return new Blob([response], { type });
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per aprire i File supportati dal browser, se non lo sono vengono scaricati
  openBlob(file: Blob, ext: string, name: string) {
    const fileURL = URL.createObjectURL(file);
    if (['.pdf', '.txt', '.csv', '.doc', '.docs', '.xls', '.xlsx', '.json', '.geojson'].includes(ext)) {
      window.open(fileURL, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = name;
      link.click();
      window.URL.revokeObjectURL(fileURL);
    }
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per il download dei File
  download(activeNode: TreeNode) {
    const fileToDownload = { ...activeNode };
    fileToDownload.ext = null;
    this.openFile(fileToDownload);
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per l'eliminazione di un file
  async elimina(selectedNodes: TreeNode[], idAtto: number) {
    // const url: string = this.utils.creaURL('fascicolo_elettronico', 'delete');
    // const params = {
    //   files: selectedNodes,
    //   idAtto
    // };
    // const res: EBWApiResponse = await this.utils.postRequest(url, params).toPromise();
    // if (res.error === 0 && res.status === 0)
    //   this.ebwModal.openInfoModal({ title: 'Eliminazione', content: res.message });
    // else this.ebwModal.openErrorModal({ title: 'Eliminazione', content: res.message });
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per l'upload di un file
  async upload(file: File, idAtto: number, nodeName: string) {
    // const url: string = this.utils.creaURL('fascicolo_elettronico', 'save');
    // const filePath = nodeName + '/' + file.name;
    // // Qui creo il file e i parametri da passare alla chiamata http
    // const formData: FormData = new FormData();
    // formData.append('file', file);
    // formData.append('filename', filePath);
    // formData.append('idAtto', idAtto.toString());
    // formData.append('tag', nodeName);
    // const res: EBWApiResponse = await this.utils.postRequest(url, formData).toPromise();
    // if (!!res.status && !!res.data && !!res.data.sameName) {
    //   return { uploaded: false, sameName: true };
    // }
    // if (!!res.status) {
    //   this.ebwModal.openInfoModal({ title: 'Upload', content: res.message });
    //   return { uploaded: false, sameName: false };
    // }
    // this.ebwModal.openInfoModal({ title: 'Upload', content: res.message });
    // return { uploaded: true };
  }

  // ------------------------------------------------------------------------------------------------

  // Metodo per sovrascrivere unh file che giá esiste
  async overwrite(file: File, idAtto: number, nodeName: string) {
    // const url: string = this.utils.creaURL('fascicolo_elettronico', 'overwrite');
    // const filePath = nodeName + '/' + file.name;
    // const formData: FormData = new FormData();
    // formData.append('file', file);
    // formData.append('filename', filePath);
    // formData.append('idAtto', idAtto.toString());
    // const res: EBWApiResponse = await this.utils.postRequest(url, formData).toPromise();
    // if (res.error === 0 && res.status === 0) {
    //   this.ebwModal.openInfoModal({ title: 'Upload', content: res.message });
    // } else {
    //   this.ebwModal.openErrorModal({ title: 'Upload', content: res.message });
    // }
  }

  // ------------------------------------------------------------------------------------------------

  // getSelectedNodes(toolbarIcon: Icon[], selectedNodes: TreeNode[]) {
  //   let temp = toolbarIcon.find((icon: Icon) => icon.tooltip === "Seleziona Tutti");
  //   temp.icon = selectedNodes.length !== 0 ? faCheckSquare : faSquareCheck;
  // }

  // ------------------------------------------------------------------------------------------------

  async downloadZip(idAtto: number) {
    // try {
    //   // l'api mette in download lo zip di tutto il fascicolo elettronico, poi il proceso é asincrono
    //   const url: string = this.utils.creaURL('fascicolo_elettronico', 'download_zip');
    //   const token: string = this.authSrv.getToken();
    //   const { status, error, message } = await this.utils.postRequest(url, { idAtto, token }).toPromise();
    //   if (!!status || !!error) {
    //     // se l'api non é andata bene
    //     throw new Error(message);
    //   }
    //   // avviso utente che il file é in lavorazione
    //   const content =
    //     'Il file é in fase di creazione, riceverai una notifica quando sará disponibile nel tuo storage personale';
    //   this.ebwModal.openInfoModal({ title: 'Download file .zip', content });
    // } catch (error) {
    //   this.ebwModal.openErrorModal({ title: 'Errore', content: error.message });
    //   console.error(error);
    // }
  }
}
